import { format } from 'date-fns';
import { BarChart3, CircleCheck, CreditCard, Info, Wallet, type LucideIcon } from 'lucide-react';
import type { AccountListQuery, AccountStatistics } from '@/api/modules/business/accountHandle';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { RequestErrorState } from '@/components/RequestErrorState';
import { formatAdAccountMoney } from '../listDisplay';

interface AdAccountStatisticsProps {
  spendTimes?: AccountListQuery['spendTimes'];
  statistics?: AccountStatistics;
  loading?: boolean;
  /** 统计失败由卡片区域接管，重试不重复请求列表 */
  errorState?: {
    message: string;
    onRetry: () => void;
  };
}

const cardClassName = 'min-w-0 gap-4 py-4 shadow-sm transition-shadow hover:shadow-md';

/** 金额兼容接口的十进制字符串，交由金额格式化区分缺失和真实零值 */
function formatStatistic(value: number | string | undefined, money = false) {
  if (money) return formatAdAccountMoney(value);
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US') : '—';
}

/** 统一数值行的字体基线，双栏或长数字仅缩小内层文字，保留外层相同行高 */
function StatisticValue({
  value,
  money = false,
  compact = false,
  primary = false,
}: {
  value?: number | string;
  money?: boolean;
  compact?: boolean;
  primary?: boolean;
}) {
  const text = formatStatistic(value, money);
  return (
    <span
      className={cn(
        'mt-auto block min-h-8 whitespace-nowrap text-2xl leading-8 font-semibold tabular-nums',
        primary && 'text-primary'
      )}
    >
      <span
        className={cn(
          'leading-none',
          text !== '—' &&
            (compact
              ? text.length > 12
                ? 'text-sm'
                : 'text-xl'
              : text.length > 16
                ? 'text-base'
                : text.length > 12
                  ? 'text-xl'
                  : undefined)
        )}
      >
        {text}
      </span>
    </span>
  );
}

/** 统一各张卡片的图标、标题高度和左右内边距 */
function StatisticsHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <CardHeader className="px-4">
      <div className="flex items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <CardTitle className="text-sm">{title}</CardTitle>
      </div>
    </CardHeader>
  );
}

/** 展示四组指标，统计失败时整块替换为统一错误提示，加载期间保留卡片骨架 */
export function AdAccountStatistics({
  spendTimes,
  statistics,
  loading = false,
  errorState,
}: AdAccountStatisticsProps) {
  if (errorState && !loading) {
    return (
      <Card role="region" aria-label="广告账户统计" className="gap-0 overflow-hidden py-0">
        {/** 局部失败沿用列表统一文案，不直接展示具体网络或服务错误 */}
        <RequestErrorState onRetry={errorState.onRetry} />
      </Card>
    );
  }

  const todayText = `截至 ${format(new Date(), 'yyyy-MM-dd')}`;
  const periodText =
    spendTimes?.beginTime && spendTimes.endTime
      ? `${spendTimes.beginTime} ~ ${spendTimes.endTime}`
      : todayText;

  return (
    <section className="@container" aria-label="广告账户统计" aria-busy={loading}>
      <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2 @5xl:grid-cols-[repeat(3,minmax(0,1fr))_minmax(20rem,1.4fr)]">
        <Card className={cardClassName}>
          <StatisticsHeader icon={BarChart3} title="广告账户总数" />
          <CardContent className="flex flex-1 items-end px-4">
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <StatisticValue value={statistics?.totalAdvertiserCount} />
            )}
          </CardContent>
          <CardFooter className="px-4 text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-4 w-28" /> : periodText}
          </CardFooter>
        </Card>

        <Card className={cardClassName}>
          <StatisticsHeader icon={CircleCheck} title="正常/禁用" />
          <CardContent className="grid flex-1 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 px-4">
            {[
              { label: '正常', value: statistics?.normalAdvertiserCount },
              { label: '禁用', value: statistics?.disabledAdvertiserCount },
            ].map(({ label, value }, index) => (
              <div key={label} className="contents">
                {index > 0 && <Separator orientation="vertical" className="h-auto" />}
                <div className="flex min-w-0 flex-col justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  {loading ? (
                    <Skeleton className="h-8 w-12" />
                  ) : (
                    <StatisticValue value={value} compact />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
          <CardFooter className="px-4 text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-4 w-28" /> : periodText}
          </CardFooter>
        </Card>

        <Card className={cardClassName}>
          <StatisticsHeader icon={CreditCard} title="广告消耗" />
          <CardContent className="flex flex-1 items-end px-4">
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <StatisticValue value={statistics?.totalAmountConsumed} money />
            )}
          </CardContent>
          <CardFooter className="px-4 text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-4 w-28" /> : periodText}
          </CardFooter>
        </Card>

        {/** 标准账户余额与高级组共享余额分别展示，不混为一个账户余额 */}
        <Card className={cardClassName}>
          <StatisticsHeader icon={Wallet} title="余额总览" />
          <CardContent className="grid flex-1 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 px-4">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium">标准资产组</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="标准资产组余额说明"
                    >
                      <Info className="size-3" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>仅汇总当前绑定的独立账户余额</TooltipContent>
                </Tooltip>
              </div>
              <span className="text-xs text-muted-foreground">仅汇总账户余额</span>
              {loading ? (
                <Skeleton className="mt-auto h-8 w-20" />
              ) : (
                <StatisticValue value={statistics?.standardBalance} money compact />
              )}
            </div>
            <Separator orientation="vertical" className="h-auto" />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-xs font-medium">高级资产组</span>
              <span className="text-xs text-muted-foreground">共享资金配额</span>
              {loading ? (
                <Skeleton className="mt-auto h-8 w-20" />
              ) : (
                <StatisticValue value={statistics?.advancedBalance} money compact primary />
              )}
            </div>
          </CardContent>
          {/** 余额为当前可用余额，不随消耗日期宣称为历史余额 */}
          <CardFooter className="px-4 text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-4 w-28" /> : todayText}
          </CardFooter>
        </Card>
      </div>
    </section>
  );
}
